import { createSlice, AnyAction } from "@reduxjs/toolkit";
import { ResponseType } from "../interfaces/response";
import { uploadProfilePhoto, getProfilePhoto, deleteProfilePhoto, updateUserProfile, getUserProfile, deleteUserAccount } from "../data-access/apis/common/profile";

interface UserState {
  user: {
    _id: string;
    name: string;
    username: string;
    email: string;
    phone: string;
    profileImage?: string | null;
    profilePhoto?: string | null;
  } | null;
  loading: boolean;
  error: string | null;
  status: ResponseType;
  statusCode: ResponseType;
  message: string | null;
  profileImage?: string | null;
  profilePhoto?: string | null;
  userProfilePhoto?: string | null;
}

const initialState: UserState = {
  user: null,
  loading: false,
  error: null,
  status: ResponseType.Pending,
  statusCode: ResponseType.Pending,
  message: null,
  profileImage: null,
  profilePhoto: null,
  userProfilePhoto: null,
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUserDetails: (state, action: AnyAction) => {
      state.user = action.payload;
    },
    clearUserDetails: (state) => {
      state.user = null;
    },
  },
  extraReducers: (builder) => {
    // 🔹 Upload Profile Photo
    builder
      .addCase(uploadProfilePhoto.pending, (state) => {
        state.loading = true;
        state.status = ResponseType.Pending;
      })
      .addCase(uploadProfilePhoto.fulfilled, (state, action) => {
        state.loading = false;
        const user = action.payload.user; // ✅ directly from API response
        state.user = {
          _id: user._id,
          name: user.name,
          username: user.username,
          email: user.email,
          phone: user.phone,
          profileImage: action.payload.profilePhoto || null, // ✅ set new photo
        };

        state.message = action.payload.message;
        state.status = ResponseType.Success;
      })
      .addCase(uploadProfilePhoto.rejected, (state, action: AnyAction) => {
        state.loading = false;
        state.error = action.payload?.message;
        state.status = ResponseType.Failed;
      })
      .addCase(getProfilePhoto.pending, (state) => {
        state.loading = true;
        state.status = ResponseType.Pending;
      })
      .addCase(getProfilePhoto.fulfilled, (state, action) => {
        state.loading = false;

        const userProfilePhoto = action.payload?.profilePhoto;

        // ✅ Set in user object or in state as needed
        if (state.user) {
          state.user.profileImage = userProfilePhoto || null;
        } else {
          state.user = {
            _id: "", // Provide fallbacks or actual values if needed
            name: "",
            username: "",
            email: "",
            phone: "",
            profileImage: userProfilePhoto || null,
          };
        }

        state.status = ResponseType.Success;
      })
      .addCase(getProfilePhoto.rejected, (state, action: AnyAction) => {
        state.loading = false;
        state.error = action.payload?.message;
        state.status = ResponseType.Failed;
      })
      .addCase(deleteProfilePhoto.pending, (state) => {
        state.loading = true;
        state.status = ResponseType.Pending;
      })
      .addCase(deleteProfilePhoto.fulfilled, (state, action) => {
        state.loading = false;
        state.status = ResponseType.Success;

        // ✅ Backend se updated user aaya hai
        const user = action.payload?.user;

        if (state.user) {
          state.user.profileImage = user?.profilePhoto || null;
        } else {
          state.user = {
            _id: user?._id || "",
            name: user?.name || "",
            username: user?.username || "",
            email: user?.email || "",
            phone: user?.phone || "",
            profileImage: user?.profilePhoto || null,
          };
        }
      })
      .addCase(deleteProfilePhoto.rejected, (state, action: AnyAction) => {
        state.loading = false;
        state.status = ResponseType.Failed;
        state.error = action.payload?.message;
      })
      .addCase(updateUserProfile.pending, (state) => {
        state.loading = true;
        state.status = ResponseType.Pending;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        const user = action.payload?.user;
        if (user) {
          state.user = {
            _id: user._id,
            name: user.name,
            username: user.username,
            email: user.email,
            phone: user.phone,
            profileImage: user.profilePhoto || null,
          };
        }
        state.message = action.payload?.message || "Profile updated successfully";
        state.status = ResponseType.Success;
      })
      .addCase(updateUserProfile.rejected, (state, action: AnyAction) => {
        state.loading = false;
        state.status = ResponseType.Failed;
        state.error = action.payload?.message;
      })
      .addCase(getUserProfile.pending, (state) => {
        state.loading = true;
        state.status = ResponseType.Pending;
      })
      .addCase(getUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.status = ResponseType.Success;
        state.user = action.payload.user;
        state.message = action.payload.message;
      })
      .addCase(getUserProfile.rejected, (state, action: AnyAction) => {
        state.loading = false;
        state.status = ResponseType.Failed;
        state.error = action.payload?.message;
      })
      .addCase(deleteUserAccount.pending, (state) => {
        state.loading = true;
        state.status = ResponseType.Pending;
      })
      .addCase(deleteUserAccount.fulfilled, (state, action) => {
        state.loading = false;
        state.status = ResponseType.Success;
        state.statusCode = ResponseType.Success;
        state.message = action.payload.message;
        state.user = null; // account delete hone ke baad user data clear
      })
      .addCase(deleteUserAccount.rejected, (state, action: AnyAction) => {
        state.loading = false;
        state.status = ResponseType.Failed;
        state.error = action.payload?.message;
      });
  },
});

export const { setUserDetails, clearUserDetails } = userSlice.actions;
export default userSlice.reducer;
